import { IAddressLookupService, IAddressLookupServiceId } from "../services/interfaces/addressLookupService";
import { IEnquiryFormService, IEnquiryFormServiceId } from "../services/interfaces/enquiryFormService";
import { ISessionService, ISessionServiceId } from "../services/interfaces/sessionService";
import { IMessagingService } from "../services/interfaces/messagingService";
import { FormComponent } from "./formComponent";
import { first } from "rxjs/operators";
import { SessionDto, ServiceResponse, ValidationError, EnquiryDto, AddressDto, FormDto, ResponseDto, CreateEnquiryDto } from "../platform/models";
import { EnquiryType, EnquiryStatus } from "../platform/enums";
import { ValidationSummaryComponent } from "./validationSummaryComponent";
import { FormFieldComponent } from "./formFieldComponent";
import { ILoggingService, ILoggingServiceId } from "../services/interfaces/loggingService";
import { IAnalyticsService, IAnalyticsServiceId } from "../services/interfaces/analyticsService";
import { IHashingService } from "../services/interfaces/hashingService";
import { EnquiryFormFailureEvent, EnquiryFormValidationErrorEvent, EnquiryFormSubmissionInteractionEvent, EnquiryFormSubmittedEvent } from "../models/analytics";
import { hide, show } from "../utils";
import { AddressView } from "../enums/addressView";
import { AnalyticsEventName } from "../enums/analyticsEventName"
import { DropdownComponent, TextboxComponent } from "./formFields";

class EnquiryFormComponent extends FormComponent {

    private responses: ResponseDto[];
    private submitBtn: HTMLButtonElement;
    private enquirySourceEl: HTMLElement;
    private enquiryPartnerIdEl: HTMLElement;
    private enquiryEmailTemplateIdEl: HTMLElement;
    private enquiryFromAddressEl: HTMLElement;
    private enquiryFromNameEl: HTMLElement;
    private enquiryQueueEl: HTMLElement;
    private enquiryProductIdEl: HTMLElement;
    private enquiryProductNameEl: HTMLElement;
    private antiForgeryToken: string;
    private formEl: HTMLElement;
    private validationSummaryEl: HTMLElement;
    private errorMessageEl: HTMLElement;
    private validationSummary: ValidationSummaryComponent;
    private accessToken: string;
    private successUrl: string;
    private analyticsTrackingService: IAnalyticsService;

    constructor(private componentEl: HTMLElement,
        messagingService: IMessagingService,
        private sessionService: ISessionService,
        private enquiryFormService: IEnquiryFormService,
        private hashingService: IHashingService,
        protected addressLookupService: IAddressLookupService,
        protected appInsights: ILoggingService,
        analyticsTrackingService: IAnalyticsService,) {
        super(componentEl, messagingService, addressLookupService, appInsights);

        this.analyticsTrackingService = analyticsTrackingService;

        this.successUrl = componentEl.dataset["formSuccessUrl"];
        this.submitBtn = componentEl.querySelector<HTMLButtonElement>(".js-enquiry-submit");
        this.enquirySourceEl = componentEl.querySelector<HTMLElement>(".js-enquiry-source");
        this.enquiryPartnerIdEl = componentEl.querySelector<HTMLElement>(".js-enquiry-partnerId");
        this.enquiryEmailTemplateIdEl = componentEl.querySelector<HTMLElement>(".js-enquiry-emailTemplateId");
        this.enquiryFromAddressEl = componentEl.querySelector<HTMLElement>(".js-enquiry-fromAddress");
        this.enquiryFromNameEl = componentEl.querySelector<HTMLElement>(".js-enquiry-fromName");
        this.enquiryQueueEl = componentEl.querySelector<HTMLElement>(".js-enquiry-queue");
        this.enquiryProductIdEl = componentEl.querySelector<HTMLElement>(".js-enquiry-productId");
        this.enquiryProductNameEl = componentEl.querySelector<HTMLElement>(".js-enquiry-productName");
        this.errorMessageEl = componentEl.querySelector<HTMLElement>(".js-enquiry-error");
        this.formEl = componentEl.querySelector<HTMLElement>(".js-form");
        this.validationSummaryEl = document.querySelector<HTMLElement>(".js-validation-summary");
        this.antiForgeryToken = (componentEl.querySelector('input[name="__RequestVerificationToken"]') as HTMLInputElement).value;

        this.submitBtn.addEventListener("click", async (submitButtonEvent: Event) => {

            this.hideErrorMessage();
            try {
                submitButtonEvent.preventDefault(); // prevent refresh
                this.submitBtn.disabled = true; // prevent multiple clicks
                this.validationSummary.clearErrors();
                const fields = this.formFields.filter(formField => formField != undefined);
                fields.forEach(field => field.hideError());
                const dropdowns = fields.filter(formField => formField instanceof DropdownComponent);
                this.responses = fields.map(f => f.getValue());
                const enquiryResponse = await this.submitEnquiry();

                if (enquiryResponse.wasUnsuccessful) {
                    this.submitBtn.disabled = false; // re-enable to allow resubmission
                    if (enquiryResponse.validationErrors.length > 0) {
                        return this.displayValidationErrorMessages(enquiryResponse.validationErrors, fields);
                    }
                    else {
                        appInsights.LogError('Enquiry form submission error: ' + enquiryResponse.errorMessage);
                        this.analyticsTrackingService.RecordEvent(AnalyticsEventName.EnquiryFormSubmitError, new EnquiryFormFailureEvent('Enquiry form submission error: ' + enquiryResponse.errorMessage));
                        return this.displayErrorMessage();
                    }
                }
                else {
                    for (const dropdown of dropdowns) {
                        const responseDto = dropdown.getValue();

                        if (responseDto && responseDto.value) {
                            this.analyticsTrackingService.RecordEvent(AnalyticsEventName.EnquiryFormSubmission, new EnquiryFormSubmissionInteractionEvent((responseDto.value as string)));
                        }
                    }

                    //bot filter
                    const enquiryCustomerReference = document.getElementById('enquirycustomerreference-field') as HTMLInputElement | null;
                    if (enquiryCustomerReference?.value) {
                        return this.redirectToSuccessPage();
                    }

                    var email = await this.hashingService.createHash(this.getResponseValue("emailaddress"));
                    var phoneNumber = await this.hashingService.createHash(this.getResponseValue("phonenumber"));
                    var firstName = await this.hashingService.createHash(this.getResponseValue("firstname"));
                    var lastName = await this.hashingService.createHash(this.getResponseValue("lastname"));

                    const addressDto = this.getResponseValue<AddressDto>("address");
                    if (addressDto != null) {
                        addressDto.city == null ? "" : addressDto.city;
                    }

                    // Only send the analytics event for legit enquirys     
                    this.analyticsTrackingService.RecordEvent(AnalyticsEventName.EnquirySuccess, new EnquiryFormSubmittedEvent(
                        // need to normalise the hashed data
                        email.trim().toLowerCase(),
                        phoneNumber.trim().toLowerCase(),
                        firstName.trim().toLowerCase(),
                        lastName.trim().toLowerCase(),
                        addressDto.addressLine1,
                        addressDto.city,
                        addressDto.city,
                        addressDto.postcode,
                        "UK"));

                    return this.redirectToSuccessPage();
                }

            } catch (exception) {
                this.submitBtn.disabled = false; // re-enable to allow resubmission
                appInsights.LogException('Enquiry form general error: ' + exception);
                this.analyticsTrackingService.RecordEvent(AnalyticsEventName.EnquiryFormSubmitError, new EnquiryFormFailureEvent('Enquiry form general error: ' + exception));
                return this.displayErrorMessage();
            }
        });

        this.formEl.addEventListener("submit", (e: Event) => {
            this.submitBtn.dispatchEvent(new Event("click"));
            e.preventDefault();
        });

        this.formEl.addEventListener('focus', async (event) => {
            const target = event.target as HTMLElement;
            const parentContainer = target.parentElement.closest<HTMLElement>(".form-field");
            if (parentContainer && event.relatedTarget) {
                const relatedTarget = event.relatedTarget as HTMLElement;
                if (relatedTarget.closest<HTMLElement>('.js-validation-errors')) {
                    // We now know ...
                    // (a) a form-field has recieved focus, and
                    // (b) we got here from validation errors
                    // ... so trigger scroll parent .form-field into view to make label etc visible
                    await new Promise(res => setTimeout(res, 0)); // Safari scroll timing quirk fix
                    parentContainer.scrollIntoView();
                }
            }
        }, true);

        this.sessionService.StartSession();
        const onSessionStarted$ = this.sessionService.OnSessionStarted$().pipe(first());
        onSessionStarted$.subscribe(async session =>
            await this.StartSession(session)
        );
    }
    private async displayValidationErrorMessages(validationErrors: ValidationError[], fields: FormFieldComponent[]) {

        const { dataset: addressFieldDataset } = this.formEl.querySelector("[data-field-id='address'][data-active-view]") as HTMLElement;
        // If search (not manual) address view, tweak validation errors
        if (addressFieldDataset.activeView === AddressView.Search) {
            const manualAddressFields = ["companyaddress1", "companycity", "companypostcode"];
            // remove individual address field validation errors, not required on search view
            validationErrors = validationErrors.filter(validationError => manualAddressFields.indexOf(validationError.fieldName.toLowerCase()) < 0);
            // add custom search address field validation error 
            const addressError = new ValidationError();
            addressError.fieldName = "address";
            validationErrors.push(addressError);
        }
        const errorsOrderedByField: ValidationError[] = [];
        // Go through form fields and compile applicable validation errors, in same order as fields
        fields.forEach(formField => {
            const validationErrorIndex = validationErrors.findIndex((validationError) => {
                return validationError.fieldName.toLowerCase() === formField.fieldId.toLowerCase();
            });
            // Match found, so add to ordered validation errors
            if (validationErrorIndex > -1) {
                const error = validationErrors[validationErrorIndex];
                if (formField.errorMessageCustom) {
                    error.errorMessage = formField.errorMessageCustom;
                }

                if (error.errorCode === "ContainsIllegalCharacters") {
                    error.errorMessage = "Please remove special characters like < >, and try again.";
                    // message is concatenation of other form fields so if there are any validation issues on other fields that will invalidate the message too
                    if (formField.fieldId.toLowerCase() == "message") {
                        error.errorMessage = "Please remove any special characters like < >, and check other fields too for < >, and try again.";
                    }
                }

                if (error.errorCode === "InvalidInput") {
                    const errorMessages = {
                        firstname: "First name can only contain letters and hyphens.",
                        lastname: "Last name can only contain letters, hyphens and apostrophes.",
                        companycity: "City can only contain letters and spaces.",
                        companypostcode: "Postcode can only contain letters, numbers and spaces.",
                        phonenumber: "Please enter a valid phone number, including area code.",
                        emailaddress: "Please use a personal email address. Internal domains are not accepted.",
                        istrading: "Indicate if you are trading or not."
                    };

                    error.errorMessage = errorMessages[formField.fieldId.toLowerCase()];
                }

                formField.showError(error);
                errorsOrderedByField.push(error);
                // Remove from original validation errors array
                validationErrors.splice(validationErrorIndex, 1)
            }
        });
        // Add any leftover validation errors array to end of ordered errors and display
        this.validationSummary.showErrors(errorsOrderedByField.concat(validationErrors));
        await new Promise(res => setTimeout(res, 300)); // Optional - Introduce slight delay for NVDA screen-reader bug to prevent list being read twice
        this.validationSummary.focusErrors();

        // push the amended list of validations errors to GTM
        this.analyticsTrackingService.RecordEvent(AnalyticsEventName.EnquiryFormValidationError, new EnquiryFormValidationErrorEvent(errorsOrderedByField));

    }

    private redirectToSuccessPage() {
        location.href = this.successUrl;
    }

    private displayErrorMessage() {
        show(this.errorMessageEl);
        this.errorMessageEl.scrollIntoView();
    }

    private hideErrorMessage() {
        hide(this.errorMessageEl);
    }

    private getResponseValue<T>(fieldId: string, defaultValue: T = null): T {
        const response = this.responses.find(response => response.fieldId === fieldId);
        if (response == null || response.value == null) {
            return defaultValue;
        }
        return response.value as T;
    }
    private async StartSession(session: SessionDto) {
        this.accessToken = session.accessToken;
        if (this.accessToken == null) {
            this.displayErrorMessage();
        }
        this.validationSummary = new ValidationSummaryComponent(this.validationSummaryEl, this.messagingService, this);
        await super.LoadDynamicFormFieldComponents(session, new FormDto());
    }
    private async submitEnquiry(): Promise<ServiceResponse<EnquiryDto>> {
        let enquiryDto = new CreateEnquiryDto();

        if (this.enquiryProductIdEl.innerText) {
            enquiryDto.productId = this.enquiryProductIdEl.innerText;
        }

        // Get nodeId to pass for server side validation
        enquiryDto.nodeId = this.componentEl.dataset["formId"] ?? "0";

        enquiryDto.productName = this.enquiryProductNameEl.innerText;
        enquiryDto.referer = document.referrer;
        enquiryDto.url = window.location.href;
        enquiryDto.privacy = this.getResponseValue("privacy", false);
        const isTradingValue = this.getResponseValue<string>("istrading", "");
        enquiryDto.IsTrading = isTradingValue === "true" ? true : isTradingValue === "false" ? false : null;

        if (enquiryDto.productName) {
            enquiryDto.enquiryTitle = "Product enquiry: " + enquiryDto.productName;
        } else {
            enquiryDto.enquiryTitle = this.getResponseValue("enquirytitle", "SE Enquiry");
        }

        enquiryDto.firstName = this.getResponseValue("firstname");
        enquiryDto.lastName = this.getResponseValue("lastname");
        enquiryDto.emailAddress = this.getResponseValue("emailaddress");
        enquiryDto.phoneNumber = this.getResponseValue("phonenumber");
        enquiryDto.status = EnquiryStatus.New;

        //bot filter
        const enquiryCustomerReference = document.getElementById('enquirycustomerreference-field') as HTMLInputElement | null;
        if (enquiryCustomerReference?.value) {
            enquiryDto.status = EnquiryStatus.Held;
        }

        enquiryDto.type = EnquiryType.Website;
        enquiryDto.source = this.enquirySourceEl.innerText;
        enquiryDto.partnerId = this.enquiryPartnerIdEl.innerText;
        enquiryDto.emailTemplateId = this.enquiryEmailTemplateIdEl.innerText;
        enquiryDto.fromAddress = this.enquiryFromAddressEl.innerText;
        enquiryDto.fromName = this.enquiryFromNameEl.innerText;
        enquiryDto.queueId = this.enquiryQueueEl.innerText;

        const addressDto = this.getResponseValue<AddressDto>("address");
        if (addressDto != null) {
            enquiryDto.companyAddress1 = addressDto.addressLine1;
            enquiryDto.companyAddress2 = addressDto.addressLine2;
            enquiryDto.companyAddress3 = addressDto.addressLine3;
            enquiryDto.companyName = addressDto.companyName == null ? "" : addressDto.companyName;
            enquiryDto.companyCity = addressDto.city == null ? "" : addressDto.city;
            enquiryDto.companyPostcode = addressDto.postcode;
        }
        enquiryDto.message = this.getFormSummary(enquiryDto);
        return await this.enquiryFormService.SubmitEnquiryForm(enquiryDto, this.antiForgeryToken);
    }

    private getFormSummary(enquiry: CreateEnquiryDto): string {
        let summaryString = "";
        const message = this.getResponseValue<string>("message");

        if (message != null && message.trim() != "") {

            summaryString += `URL: ${enquiry.url} \n`;
            summaryString += `Referrer: ${enquiry.referer} \n`;
            summaryString += `Product Id: ${enquiry.productId} \n`;
            summaryString += `Product Name: ${enquiry.productName} \n`;
            summaryString += `Trading: ${enquiry.IsTrading} \n`;
            summaryString += `First name: ${enquiry.firstName} \n`;
            summaryString += `Last name: ${enquiry.lastName} \n`;
            summaryString += `Email address: ${enquiry.emailAddress} \n`;
            summaryString += `Phone number: ${enquiry.phoneNumber} \n`;
            summaryString += `Company Address: ${this.getAddressString(enquiry)} \n`;
            summaryString += `Message: ${message} \n`;
            summaryString += `Privacy: ${enquiry.privacy} \n`;

        }


        return summaryString;
    }

    private getAddressString(enquiry: CreateEnquiryDto) {
        let addressArray = [];
        addressArray.push(enquiry.companyName);
        addressArray.push(enquiry.companyAddress1);
        addressArray.push(enquiry.companyAddress2);
        addressArray.push(enquiry.companyAddress3);
        addressArray.push(enquiry.companyCity);
        addressArray.push(enquiry.companyPostcode);
        addressArray = addressArray.filter(addressItem => addressItem != null && addressItem != "");
        const addressString = addressArray.join(", ");
        return addressString;
    }
}

export { EnquiryFormComponent };